/**
 * Conversor de erros (tRPC / fetch / zod / Error) em mensagens amigáveis em PT-BR.
 *
 * Usar SEMPRE em toast.error(...) em vez de cuspir err.message direto, para evitar:
 *  - vazamento de stack/paths internos do servidor
 *  - mensagens em inglês ou JSON cru
 *  - "undefined" / "[object Object]" indo parar na UI
 *
 * Uso:
 *   onError: (err) => toast.error(toUserFriendlyError(err))
 *   onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao salvar contato"))
 */

type AnyErr = unknown;

const TRPC_CODE_MESSAGES: Record<string, string> = {
  UNAUTHORIZED: "Sua sessão expirou. Faça login novamente.",
  FORBIDDEN: "Você não tem permissão para esta ação.",
  NOT_FOUND: "Recurso não encontrado.",
  CONFLICT: "Já existe um registro com esses dados.",
  TOO_MANY_REQUESTS: "Muitas tentativas. Aguarde um instante e tente novamente.",
  TIMEOUT: "A operação demorou demais. Tente novamente.",
  PAYLOAD_TOO_LARGE: "Arquivo grande demais para envio.",
  BAD_REQUEST: "Dados inválidos. Verifique os campos preenchidos.",
  UNPROCESSABLE_CONTENT: "Dados inválidos. Verifique os campos preenchidos.",
  INTERNAL_SERVER_ERROR: "Falha interna do servidor. Nossa equipe foi notificada.",
  SERVICE_UNAVAILABLE: "Serviço temporariamente indisponível. Tente em alguns minutos.",
};

const NETWORK_HINTS = [
  "Failed to fetch",
  "NetworkError",
  "ERR_NETWORK",
  "ECONNREFUSED",
  "ETIMEDOUT",
  "Network request failed",
];

const INTERNAL_LEAK_PATTERNS = [
  /\bat\s+\/?[A-Za-z0-9_\-./]+:\d+/, // stack trace path:line
  /\b(Error|TypeError|ReferenceError|RangeError|SyntaxError):\s/,
  /Cannot read properties? of (?:undefined|null)/i,
  /\bundefined is not (?:a function|an object)\b/i,
  /\bdrizzle|tsx|tRPC server/i,
  /\bsql:\s/i,
];

function looksLikeInternalLeak(msg: string): boolean {
  if (!msg) return false;
  if (INTERNAL_LEAK_PATTERNS.some((rx) => rx.test(msg))) return true;
  // mensagens genéricas em inglês de runtime
  if (/^[A-Z][a-zA-Z]*Error:/.test(msg)) return true;
  return false;
}

export function toUserFriendlyError(error: AnyErr, fallback?: string): string {
  const fb = fallback ?? "Não conseguimos completar a ação. Tente novamente.";

  if (!error) return fb;

  // String direta
  if (typeof error === "string") {
    if (looksLikeInternalLeak(error)) return fb;
    return error;
  }

  const e = error as { message?: string; data?: { code?: string; httpStatus?: number; zodError?: unknown } | null; code?: string; cause?: unknown };
  const message = typeof e?.message === "string" ? e.message : "";
  const trpcCode = e?.data?.code ?? (typeof e?.code === "string" ? e.code : undefined);
  const httpStatus = e?.data?.httpStatus;

  // 1) Erros de rede
  if (NETWORK_HINTS.some((h) => message.includes(h))) {
    return "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
  }

  // 2) Erros de validação Zod do tRPC — extrai primeira mensagem útil
  const zodError = e?.data?.zodError as { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | undefined;
  if (zodError) {
    const firstField = zodError.fieldErrors && Object.keys(zodError.fieldErrors)[0];
    if (firstField && zodError.fieldErrors?.[firstField]?.[0]) {
      return `${firstField}: ${zodError.fieldErrors[firstField][0]}`;
    }
    if (zodError.formErrors?.[0]) return zodError.formErrors[0];
    return "Verifique os campos preenchidos.";
  }

  // 3) Códigos tRPC mapeados
  if (trpcCode && TRPC_CODE_MESSAGES[trpcCode]) {
    // Quando o backend deixou uma mensagem em PT-BR sem leak, preferimos a dele
    if (message && /[áéíóúâêîôûãõç]/i.test(message) && !looksLikeInternalLeak(message)) {
      return message;
    }
    return TRPC_CODE_MESSAGES[trpcCode];
  }

  // 4) HTTP status (sem código tRPC)
  if (typeof httpStatus === "number") {
    if (httpStatus === 401) return TRPC_CODE_MESSAGES.UNAUTHORIZED;
    if (httpStatus === 403) return TRPC_CODE_MESSAGES.FORBIDDEN;
    if (httpStatus === 404) return TRPC_CODE_MESSAGES.NOT_FOUND;
    if (httpStatus === 409) return TRPC_CODE_MESSAGES.CONFLICT;
    if (httpStatus === 413) return TRPC_CODE_MESSAGES.PAYLOAD_TOO_LARGE;
    if (httpStatus === 429) return TRPC_CODE_MESSAGES.TOO_MANY_REQUESTS;
    if (httpStatus >= 500) return TRPC_CODE_MESSAGES.INTERNAL_SERVER_ERROR;
  }

  // 5) Mensagem direta — só usa se não parecer leak interno
  if (message && !looksLikeInternalLeak(message)) {
    return message;
  }

  return fb;
}
